import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Layout,
  Banner,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  Divider
} from '@shopify/polaris';
import { ChevronLeftIcon } from '@shopify/polaris-icons';
import { openprojectService } from '../services/openprojectService';
import LoadingState from './shared/LoadingState';
import PageError from './shared/PageError';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userWorkPackages, setUserWorkPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUserDetails();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserDetails = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Get user details
      const users = await openprojectService.getUsers();
      const foundUser = users.find(u => u.id.toString() === userId);
      
      if (!foundUser) {
        setError('User not found');
        return;
      }
      
      setUser(foundUser);

      // Get user's work packages
      try {
        const workPackagesResponse = await openprojectService.getAllWorkPackages(`[{"assignee":{"operator":"=","values":["${userId}"]}}]`);
        setUserWorkPackages(workPackagesResponse._embedded?.elements || []);
      } catch (wpError) {
        console.warn('Could not load work packages for user:', wpError);
      }

    } catch (error) {
      console.error('Failed to load user details:', error);
      setError(error.response?.data?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Page title="User Details">
        <LoadingState />
      </Page>
    );
  }

  if (error) {
    return (
      <Page
        title="User Details"
        primaryAction={{ content: 'Retry', onAction: loadUserDetails }}
        secondaryActions={[{ content: 'Back to Users', icon: ChevronLeftIcon, onAction: () => navigate('/users') }]}
      >
        <PageError>{error}</PageError>
      </Page>
    );
  }

  if (!user) {
    return (
      <Page title="User Details">
        <Layout>
          <Layout.Section>
            <Banner tone="warning">User not found</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={user.name || 'Unknown User'}
      subtitle={`User ID: ${user.id}`}
      primaryAction={{
        content: 'Refresh',
        onAction: loadUserDetails
      }}
      secondaryActions={[
        {
          content: 'Back to Users',
          icon: ChevronLeftIcon,
          onAction: () => navigate('/users')
        }
      ]}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">User Information</Text>
              <InlineStack align="space-between">
                <div>
                  <Text variant="headingSm" as="h6">Name</Text>
                  <Text variant="bodyMd">{user.name || 'N/A'}</Text>
                </div>
                <div>
                  <Text variant="headingSm" as="h6">Email</Text>
                  <Text variant="bodyMd">{user.email || 'N/A'}</Text>
                </div>
              </InlineStack>

              <InlineStack align="space-between">
                <div>
                  <Text variant="headingSm" as="h6">Login</Text>
                  <Text variant="bodyMd">{user.login || 'N/A'}</Text>
                </div>
                <div>
                  <Text variant="headingSm" as="h6">Status</Text>
                  <Badge tone={user.status === 'active' ? 'success' : 'warning'}>
                    {user.status || 'Unknown'}
                  </Badge>
                </div>
              </InlineStack>

              <InlineStack align="space-between">
                <div>
                  <Text variant="headingSm" as="h6">Admin</Text>
                  <Badge tone={user.admin ? 'success' : 'info'}>
                    {user.admin ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div>
                  <Text variant="headingSm" as="h6">Created</Text>
                  <Text variant="bodyMd">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : 'N/A'}
                  </Text>
                </div>
              </InlineStack>

              {user.updatedAt && (
                <div>
                  <Text variant="headingSm" as="h6">Last Updated</Text>
                  <Text variant="bodyMd">
                    {new Date(user.updatedAt).toLocaleDateString('en-US')}
                  </Text>
                </div>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Work Packages ({userWorkPackages.length})</Text>
              {userWorkPackages.length > 0 ? (
              <BlockStack gap="300">
                {userWorkPackages.slice(0, 10).map((wp, index) => (
                  <div key={wp.id}>
                    <InlineStack align="space-between">
                      <div>
                        <Text variant="headingSm" as="h6">#{wp.id} - {wp.subject}</Text>
                        <Text variant="bodyMd" tone="subdued">
                          Type: {wp._links?.type?.title || 'Unknown'} |
                          Status: {wp._links?.status?.title || 'Unknown'}
                        </Text>
                      </div>
                      <Badge tone={wp._links?.status?.title?.toLowerCase().includes('done') ? 'success' : 'info'}>
                        {wp._links?.status?.title || 'Unknown'}
                      </Badge>
                    </InlineStack>
                    {index < userWorkPackages.slice(0, 10).length - 1 && <Divider />}
                  </div>
                ))}
                {userWorkPackages.length > 10 && (
                  <Text variant="bodyMd" tone="subdued">
                    ... and {userWorkPackages.length - 10} more work packages
                  </Text>
                )}
              </BlockStack>
            ) : (
              <Text variant="bodyMd" tone="subdued">
                No work packages assigned to this user
              </Text>
            )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default UserDetail;
